import { useEffect, useState } from 'react';
import { 
  Award, Star, Gift, Settings, Plus, Users, 
  TrendingUp, Coins, Edit, Trash2, Save, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';

const tierColors = ['bg-amber-100 text-amber-700', 'bg-gray-100 text-gray-600', 'bg-yellow-100 text-yellow-700', 'bg-purple-100 text-purple-700'];

const rewardTypes = [
  { value: 'discount_percentage', label: 'Percentage Off' },
  { value: 'discount_fixed', label: 'Fixed Amount Off' },
  { value: 'free_shipping', label: 'Free Shipping' },
  { value: 'free_product', label: 'Free Product' },
];

export default function Loyalty() {
  const [program, setProgram] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [showRewardDialog, setShowRewardDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [editingReward, setEditingReward] = useState(null);

  useEffect(() => {
    fetchLoyaltyProgram();
  }, []);

  const fetchLoyaltyProgram = async () => {
    setLoading(true);
    try {
      const res = await api.get('/enterprise/loyalty/program');
      setProgram(res.data.program);
      setTiers(res.data.tiers || []);
      setRewards(res.data.rewards || []);
    } catch (error) {
      toast.error('Failed to load loyalty program');
    } finally {
      setLoading(false);
    }
  };

  const updateProgram = async () => {
    setSaving(true);
    try {
      await api.put('/enterprise/loyalty/program', program);
      toast.success('Program settings saved');
    } catch (error) {
      toast.error('Failed to update program');
    } finally {
      setSaving(false);
    }
  };

  const toggleProgram = async () => {
    try {
      await api.put('/enterprise/loyalty/program', { is_active: !program?.is_active });
      setProgram({ ...program, is_active: !program?.is_active });
      toast.success(program?.is_active ? 'Program deactivated' : 'Program activated');
    } catch (error) {
      toast.error('Failed to toggle program');
    }
  };

  // Tier CRUD
  const saveTier = async () => {
    try {
      if (editingTier?.tier_id) {
        await api.put(`/enterprise/loyalty/tiers/${editingTier.tier_id}`, editingTier);
        toast.success('Tier updated');
      } else {
        await api.post('/enterprise/loyalty/tiers', editingTier);
        toast.success('Tier created');
      }
      setShowTierDialog(false);
      setEditingTier(null);
      fetchLoyaltyProgram();
    } catch (error) {
      toast.error('Failed to save tier');
    }
  };

  const deleteTier = async (tierId) => {
    if (!confirm('Delete this tier?')) return;
    try {
      await api.delete(`/enterprise/loyalty/tiers/${tierId}`);
      toast.success('Tier deleted');
      fetchLoyaltyProgram();
    } catch (error) {
      toast.error('Failed to delete tier');
    }
  };

  // Reward CRUD
  const saveReward = async () => {
    try {
      if (editingReward?.reward_id) {
        await api.put(`/enterprise/loyalty/rewards/${editingReward.reward_id}`, editingReward);
        toast.success('Reward updated');
      } else {
        await api.post('/enterprise/loyalty/rewards', editingReward);
        toast.success('Reward created');
      }
      setShowRewardDialog(false);
      setEditingReward(null);
      fetchLoyaltyProgram();
    } catch (error) {
      toast.error('Failed to save reward');
    }
  };

  const deleteReward = async (rewardId) => {
    if (!confirm('Delete this reward?')) return;
    try {
      await api.delete(`/enterprise/loyalty/rewards/${rewardId}`);
      toast.success('Reward deleted');
      fetchLoyaltyProgram();
    } catch (error) {
      toast.error('Failed to delete reward');
    }
  };

  const toggleRewardActive = async (reward) => {
    try {
      await api.put(`/enterprise/loyalty/rewards/${reward.reward_id}`, {
        is_active: !reward.is_active
      });
      toast.success(reward.is_active ? 'Reward deactivated' : 'Reward activated');
      fetchLoyaltyProgram();
    } catch (error) {
      toast.error('Failed to toggle reward');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" data-testid="loyalty-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium text-stone-900">Loyalty Program</h1>
            <p className="text-stone-500 mt-1">Manage rewards and customer loyalty</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-500">
                {program?.is_active ? 'Active' : 'Inactive'}
              </span>
              <Switch
                checked={program?.is_active}
                onCheckedChange={toggleProgram}
              />
            </div>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Members</p>
                  <p className="text-2xl font-semibold">0</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Coins className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Points Issued</p>
                  <p className="text-2xl font-semibold">0</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Gift className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Rewards Redeemed</p>
                  <p className="text-2xl font-semibold">0</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Points Rate</p>
                  <p className="text-2xl font-semibold">{program?.points_per_dollar || 1}x</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="tiers">
              <Award className="h-4 w-4 mr-2" />
              Tiers ({tiers.length})
            </TabsTrigger>
            <TabsTrigger value="rewards">
              <Gift className="h-4 w-4 mr-2" />
              Rewards ({rewards.length})
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Program Settings</CardTitle>
                <CardDescription>Configure how customers earn and redeem points</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Program Name</Label>
                    <Input 
                      value={program?.name || ''} 
                      onChange={(e) => setProgram({...program, name: e.target.value})}
                      placeholder="Tulip Rewards"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Points Currency Name</Label>
                    <Input 
                      value={program?.points_currency_name || ''} 
                      onChange={(e) => setProgram({...program, points_currency_name: e.target.value})}
                      placeholder="Tulip Points"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Points per SGD spent</Label>
                    <Input 
                      type="number"
                      value={program?.points_per_dollar || 1} 
                      onChange={(e) => setProgram({...program, points_per_dollar: parseFloat(e.target.value)})}
                    />
                    <p className="text-xs text-stone-500">
                      Customers earn {program?.points_per_dollar || 1} point(s) for every $1 spent
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Points Expiry (months)</Label>
                    <Input 
                      type="number"
                      value={program?.points_expiry_months || 12} 
                      onChange={(e) => setProgram({...program, points_expiry_months: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Minimum Redemption (points)</Label>
                    <Input 
                      type="number"
                      value={program?.minimum_redemption || 100} 
                      onChange={(e) => setProgram({...program, minimum_redemption: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="pt-4">
                  <Button onClick={updateProgram} disabled={saving}>
                    {saving ? <span className="animate-pulse">Saving...</span> : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tiers Tab */}
          <TabsContent value="tiers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Loyalty Tiers</CardTitle>
                    <CardDescription>Define membership levels and benefits</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingTier({ name: '', minimum_points: 0, multiplier: 1.0, benefits: [], position: tiers.length });
                    setShowTierDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Tier
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {tiers.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="h-12 w-12 mx-auto text-stone-300 mb-4" />
                    <p className="text-stone-500 mb-4">No tiers configured yet</p>
                    <Button variant="outline" onClick={() => {
                      setEditingTier({ name: 'Bronze', minimum_points: 0, multiplier: 1.0, benefits: ['Welcome bonus'], position: 0 });
                      setShowTierDialog(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Tier
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tiers.sort((a, b) => a.position - b.position).map((tier, index) => (
                      <div key={tier.tier_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${tierColors[index % tierColors.length]}`}>
                            <Star className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium">{tier.name}</p>
                            <p className="text-sm text-stone-500">
                              {tier.minimum_points}+ points • {tier.multiplier}x multiplier
                            </p>
                            {tier.benefits?.length > 0 && (
                              <p className="text-xs text-stone-400 mt-1">
                                Benefits: {tier.benefits.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => {
                            setEditingTier(tier);
                            setShowTierDialog(true);
                          }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteTier(tier.tier_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Redemption Rewards</CardTitle>
                    <CardDescription>What customers can redeem points for</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingReward({ name: '', description: '', type: 'discount_percentage', value: 10, points_required: 100, is_active: true });
                    setShowRewardDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Reward
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {rewards.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="h-12 w-12 mx-auto text-stone-300 mb-4" />
                    <p className="text-stone-500 mb-4">No rewards configured yet</p>
                    <Button variant="outline" onClick={() => {
                      setEditingReward({ name: '$5 Off', description: 'Get $5 off your next order', type: 'discount_fixed', value: 5, points_required: 100, is_active: true });
                      setShowRewardDialog(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Reward
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reward</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Points Required</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[100px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rewards.map((reward) => (
                        <TableRow key={reward.reward_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{reward.name}</p>
                              {reward.description && (
                                <p className="text-xs text-stone-500">{reward.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">
                            {rewardTypes.find(t => t.value === reward.type)?.label || reward.type}
                          </TableCell>
                          <TableCell>
                            {reward.type === 'discount_percentage' ? `${reward.value}%` :
                             reward.type === 'discount_fixed' ? `$${reward.value}` :
                             reward.type === 'free_shipping' ? 'Free' : reward.value}
                          </TableCell>
                          <TableCell>{reward.points_required} pts</TableCell>
                          <TableCell>
                            <Switch
                              checked={reward.is_active}
                              onCheckedChange={() => toggleRewardActive(reward)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => {
                                setEditingReward(reward);
                                setShowRewardDialog(true);
                              }}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteReward(reward.reward_id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Tier Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTier?.tier_id ? 'Edit Tier' : 'Create Tier'}</DialogTitle>
            <DialogDescription>Configure loyalty tier settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tier Name</Label>
              <Input
                value={editingTier?.name || ''}
                onChange={(e) => setEditingTier({...editingTier, name: e.target.value})}
                placeholder="Gold"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum Points</Label>
                <Input
                  type="number"
                  value={editingTier?.minimum_points || 0}
                  onChange={(e) => setEditingTier({...editingTier, minimum_points: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Points Multiplier</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editingTier?.multiplier || 1}
                  onChange={(e) => setEditingTier({...editingTier, multiplier: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Benefits (comma separated)</Label>
              <Input
                value={(editingTier?.benefits || []).join(', ')}
                onChange={(e) => setEditingTier({...editingTier, benefits: e.target.value.split(',').map(b => b.trim()).filter(Boolean)})}
                placeholder="Free shipping, Early access, Birthday bonus"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTierDialog(false)}>Cancel</Button>
            <Button onClick={saveTier}>Save Tier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reward Dialog */}
      <Dialog open={showRewardDialog} onOpenChange={setShowRewardDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingReward?.reward_id ? 'Edit Reward' : 'Create Reward'}</DialogTitle>
            <DialogDescription>Configure redemption reward</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reward Name</Label>
              <Input
                value={editingReward?.name || ''}
                onChange={(e) => setEditingReward({...editingReward, name: e.target.value})}
                placeholder="$10 Off Your Order"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={editingReward?.description || ''}
                onChange={(e) => setEditingReward({...editingReward, description: e.target.value})}
                placeholder="Get $10 off your next purchase"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reward Type</Label>
                <Select 
                  value={editingReward?.type || 'discount_percentage'}
                  onValueChange={(v) => setEditingReward({...editingReward, type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rewardTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Value</Label>
                <Input
                  type="number"
                  value={editingReward?.value || 0}
                  onChange={(e) => setEditingReward({...editingReward, value: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Points Required</Label>
              <Input
                type="number"
                value={editingReward?.points_required || 100}
                onChange={(e) => setEditingReward({...editingReward, points_required: parseInt(e.target.value)})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRewardDialog(false)}>Cancel</Button>
            <Button onClick={saveReward}>Save Reward</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
